import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface MemoryPhoto {
  id: string;
  photo_url: string;
  member_name: string;
  team: string;
  caption: string;
  likes: number;
  liked_by: string[];
  created_at: string;
}

export function useMemoryWall() {
  const [photos, setPhotos] = useState<MemoryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchPhotos = useCallback(async () => {
    const { data, error } = await supabase
      .from('memory_photos')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setPhotos(data as MemoryPhoto[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPhotos();

    const channel = supabase
      .channel('memory_photos_rt_v2')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'memory_photos' }, (payload) => {
        setPhotos(prev => {
          if (prev.some(p => p.id === (payload.new as MemoryPhoto).id)) return prev;
          return [payload.new as MemoryPhoto, ...prev];
        });
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'memory_photos' }, (payload) => {
        setPhotos(prev =>
          prev.map(p =>
            p.id === (payload.new as MemoryPhoto).id
              ? { ...p, likes: (payload.new as MemoryPhoto).likes, liked_by: (payload.new as MemoryPhoto).liked_by }
              : p,
          ),
        );
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'memory_photos' }, (payload) => {
        setPhotos(prev => prev.filter(p => p.id !== (payload.old as { id: string }).id));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchPhotos]);

  /** 上傳照片 — 本地 optimistic insert 確保上傳者自己立即看到 */
  const uploadPhoto = useCallback(async (
    file: File,
    memberName: string,
    team: string,
    caption: string,
  ): Promise<boolean> => {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: storageErr } = await supabase.storage
        .from('memory-wall')
        .upload(path, file, { contentType: file.type, upsert: false });

      if (storageErr) throw storageErr;

      const { data: urlData } = supabase.storage
        .from('memory-wall')
        .getPublicUrl(path);

      const { data: inserted, error: dbErr } = await supabase
        .from('memory_photos')
        .insert({
          photo_url: urlData.publicUrl,
          member_name: memberName,
          team,
          caption,
        })
        .select()
        .maybeSingle();

      if (dbErr) throw dbErr;

      // Optimistic local update (realtime 可能有延遲，這裡確保上傳者立刻看到)
      if (inserted) {
        setPhotos(prev => {
          if (prev.some(p => p.id === (inserted as MemoryPhoto).id)) return prev;
          return [inserted as MemoryPhoto, ...prev];
        });
      }

      // ── 系統通知卡 ──────────────────────────────────────────────
      const notifMsg  = `📸 ${memberName} 上傳了一張照片到回憶牆！`;
      const commentId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // 用 comment-${commentId} 讓 NotificationToast 去重：
      // useComments realtime 收到 INSERT 後也會用相同 ID dispatch，
      // addedIds 會擋掉第二次，避免重複跳出通知
      window.dispatchEvent(new CustomEvent('srs-notification', {
        detail: {
          id: `comment-${commentId}`,
          type: 'system',
          displayName: '⚡系統',
          team: (team as 'black' | 'white') ?? null,
          message: notifMsg,
          timestamp: Date.now(),
        },
      }));

      // ── 留言區系統公告 ──────────────────────────────────────────
      await supabase.from('comments').insert({
        id: commentId,
        member_id: 'system',
        display_name: '⚡系統',
        team: team ?? null,
        message: notifMsg,
        created_at: Date.now(),
      });

      return true;
    } catch {
      return false;
    } finally {
      setUploading(false);
    }
  }, []);

  /** 切換愛心（透過原子 RPC） */
  const toggleLike = useCallback(async (photoId: string, memberName: string) => {
    // Optimistic UI：先本地更新
    setPhotos(prev => prev.map(p => {
      if (p.id !== photoId) return p;
      const isLiked = p.liked_by.includes(memberName);
      return {
        ...p,
        likes: isLiked ? Math.max(0, p.likes - 1) : p.likes + 1,
        liked_by: isLiked
          ? p.liked_by.filter(n => n !== memberName)
          : [...p.liked_by, memberName],
      };
    }));

    // 打 RPC 做原子更新
    await supabase.rpc('toggle_photo_like', {
      p_photo_id: photoId,
      p_member_name: memberName,
    });
  }, []);

  /** 刪除自己的照片（同步清除 Storage + DB） */
  const deletePhoto = useCallback(async (photo: MemoryPhoto): Promise<boolean> => {
    // Optimistic local remove
    setPhotos(prev => prev.filter(p => p.id !== photo.id));
    try {
      // 從 URL 提取 storage path
      const storagePath = new URL(photo.photo_url).pathname
        .split('/memory-wall/').pop() ?? '';

      // 並行刪除 DB + Storage
      const [{ error: dbErr }] = await Promise.all([
        supabase.from('memory_photos').delete().eq('id', photo.id),
        storagePath
          ? supabase.storage.from('memory-wall').remove([storagePath])
          : Promise.resolve({ error: null }),
      ]);

      if (dbErr) throw dbErr;
      return true;
    } catch {
      // 失敗時恢復
      setPhotos(prev => [photo, ...prev].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ));
      return false;
    }
  }, []);

  /** 清除所有照片（DB + Storage 盡力清除） */
  const clearAllPhotos = useCallback(async (): Promise<boolean> => {
    try {
      // 先抓所有照片的 URL 備用 storage 清除
      const { data } = await supabase
        .from('memory_photos')
        .select('photo_url');

      // 刪除 DB 所有紀錄
      await supabase
        .from('memory_photos')
        .delete()
        .gte('created_at', '1900-01-01T00:00:00Z');

      // 清除本地狀態
      setPhotos([]);

      // 盡力清除 Storage 檔案
      if (data && data.length > 0) {
        const paths = (data as { photo_url: string }[])
          .map(p => {
            try {
              return new URL(p.photo_url).pathname.split('/memory-wall/').pop() ?? '';
            } catch { return ''; }
          })
          .filter(Boolean);
        if (paths.length > 0) {
          supabase.storage.from('memory-wall').remove(paths).catch(() => {});
        }
      }

      return true;
    } catch {
      return false;
    }
  }, []);

  return { photos, loading, uploading, uploadPhoto, toggleLike, deletePhoto, clearAllPhotos };
}
